import { useCallback, useEffect, useRef, useState } from 'react';
import { ListPayload } from 'core/http/response';
import useSession from 'hooks/useSession';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ArchivedTemplatesParams, getArchivedTemplates, getTemplates } from 'store/template/client';
import { TemplateResponse } from 'store/template/template';
import { TableChangeParams } from 'utils/tableHelpers';
import { toDeclarationExternalType } from '../toDeclarationExternalType';

const emptyTemplates: ListPayload<TemplateResponse> = { list: [], pageNumber: 0, pageSize: 0, total: 0 };

interface Props {
    country: DeclarationCountry;
    internalType: DeclarationInternalType;
    formType: MessageTypes;
    search?: string;
    atArchivedPath?: boolean;
    withPagination?: boolean;
    enabled?: boolean;
}

const useTemplateList = ({
    country,
    internalType,
    formType,
    search,
    atArchivedPath,
    withPagination = true,
    enabled = true,
}: Props) => {
    const { customerId } = useSession();
    const [templates, setTemplates] = useState(emptyTemplates);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    // Only the response of the most recent request may update the state.
    const latestRequestId = useRef(0);

    const request = useCallback(
        (params?: TableChangeParams) => {
            const size = { size: withPagination ? 10 : 9999 };
            // An empty search sends no query parameter at all.
            const query = search || undefined;

            if (!atArchivedPath) {
                return getTemplates(country, internalType, formType, { ...size, query, ...params });
            }

            const archivedParams: ArchivedTemplatesParams = {
                declarationExternalType: toDeclarationExternalType(country, internalType),
                declarationInternalType: country === 'uk' && internalType === 'IMPORT' ? 'IMPORT_NEW' : internalType,
                declarationName: formType,
                query,
                ...size,
                ...params,
            };
            // Irish ENS templates all share the declarationName 'ENS', so it carries no
            // information. ICS2 ENS templates are distinguished by F11 vs F50, so keep it.
            if (internalType === 'ENS' && country !== 'eu') {
                delete archivedParams.declarationName;
            }
            return getArchivedTemplates(archivedParams);
        },
        [atArchivedPath, country, internalType, formType, search, withPagination]
    );

    const refetch = useCallback(
        async (params?: TableChangeParams) => {
            const requestId = ++latestRequestId.current;
            if (!enabled) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                const response = await request(params);
                if (requestId !== latestRequestId.current) return;
                setTemplates(response);
            } catch (e) {
                if (requestId !== latestRequestId.current) return;
                setTemplates(emptyTemplates);
                setError(e);
            } finally {
                if (requestId === latestRequestId.current) setIsLoading(false);
            }
        },
        [enabled, request]
    );

    // A different tenant, country, type or form must never show the rows of the previous one,
    // so the list is cleared before loading. Paging, searching and refetching after a save keep the rows.
    useEffect(() => {
        setTemplates(emptyTemplates);
    }, [customerId, country, internalType, formType, atArchivedPath, enabled]);

    useEffect(() => {
        refetch();
    }, [customerId, refetch]);

    return { templates, isLoading, error, refetch };
};

export default useTemplateList;
